// src/lib/media.ts — the media library's API (/api/v1/media, src/modules/media; docs/media-library.md), the
// authenticated file fetches the grid needs (thumbnails, previews, downloads, zips), an upload that reports progress,
// and the copy / cut clipboard shared by every grid on the page.
import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { api, apiUrl, authedFetch, tokenStore, ApiRequestError } from './api';

export type FolderKind = 'PROPERTY' | 'PROPERTY_GALLERY' | 'UNIT' | 'UNIT_PHOTOS' | 'UNIT_FLOORPLANS' | 'UNIT_EPC' | 'CUSTOM';
export type MediaKind = 'IMAGE' | 'PDF' | 'VIDEO';

export interface MediaFolder {
  id: string; parentId: string | null; name: string; kind: FolderKind; isAuto: boolean;
  propertyId: string | null; unitId: string | null; sort: number; archived: boolean; archivedAt: string | null;
  itemCount?: number; createdAt: string; updatedAt: string; children?: MediaFolder[];
}
export interface MediaFolderDetail extends MediaFolder {
  path: Array<{ id: string; name: string; kind: FolderKind }>;
  children: MediaFolder[];
}
export interface MediaItem {
  id: string; folderId: string; propertyId: string | null; fileName: string; mimeType: string; kind: MediaKind; sizeBytes: number;
  width: number | null; height: number | null; durationS: number | null;
  label: string | null; labelIndex: number | null; displayLabel: string | null; caption: string | null; sort: number; isCover: boolean;
  url: string; thumbUrl: string; downloadUrl: string; createdAt: string; updatedAt: string;
}
export interface MediaLabel { name: string; isSystem: boolean }

/** Folders whose first photo is the cover the website and app show. */
export const COVER_FOLDERS: ReadonlySet<FolderKind> = new Set(['UNIT_PHOTOS', 'PROPERTY_GALLERY']);
/** Floorplans and EPCs are labelled by their folder: no label picker there. */
export const UNLABELLED_FOLDERS: ReadonlySet<FolderKind> = new Set(['UNIT_FLOORPLANS', 'UNIT_EPC']);
/** What the file pickers accept (the server checks again, by the file's first bytes). */
export const MEDIA_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,.heic,.heif,application/pdf,video/mp4,video/quicktime,video/webm,.mov';
const MB = 1024 * 1024;
export const MEDIA_LIMITS: Record<MediaKind, number> = { IMAGE: 15 * MB, PDF: 25 * MB, VIDEO: 500 * MB };

export const mediaKindOf = (mime: string, name = ''): MediaKind | null => {
  const m = mime.toLowerCase();
  if (m.startsWith('image/') || /\.(heic|heif)$/i.test(name)) return 'IMAGE';
  if (m === 'application/pdf' || /\.pdf$/i.test(name)) return 'PDF';
  if (m.startsWith('video/') || /\.(mov|mp4|m4v|webm)$/i.test(name)) return 'VIDEO';
  return null;
};
/** Why a file would be refused before it is sent, or null. */
export function checkUpload(file: File): string | null {
  const k = mediaKindOf(file.type, file.name);
  if (!k) return 'Not an image, PDF or video';
  if (!file.size) return 'The file is empty';
  if (file.size > MEDIA_LIMITS[k]) return `Too large — ${k === 'IMAGE' ? 'an image' : k === 'PDF' ? 'a PDF' : 'a video'} can be at most ${MEDIA_LIMITS[k] / MB} MB`;
  return null;
}
export const formatBytes = (n: number) => n < 1024 ? `${n} B` : n < MB ? `${Math.round(n / 1024)} KB` : `${(n / MB).toFixed(n < 10 * MB ? 1 : 0)} MB`;

export const mediaApi = {
  labels: async () => (await api.get<MediaLabel[]>('/media/labels')).data,
  tree: async (q: { propertyId?: string; unitId?: string; includeArchived?: boolean } = {}) => (await api.get<MediaFolder[]>('/media/folders', q)).data,
  folder: async (id: string) => (await api.get<MediaFolderDetail>(`/media/folders/${id}`)).data,
  createFolder: async (body: { parentId?: string | null; name: string }) => (await api.post<MediaFolder>('/media/folders', body)).data,
  renameFolder: async (id: string, name: string) => (await api.patch<MediaFolder>(`/media/folders/${id}`, { name })).data,
  deleteFolder: (id: string) => api.delete(`/media/folders/${id}`),
  items: async (folderId: string) => (await api.get<MediaItem[]>(`/media/folders/${folderId}/items`)).data,
  updateItem: async (id: string, body: { label?: string | null; caption?: string | null; fileName?: string }) => (await api.patch<MediaItem>(`/media/items/${id}`, body)).data,
  reorder: async (folderId: string, order: string[]) => (await api.post<MediaItem[]>('/media/items/reorder', { folderId, order })).data,
  copy: async (ids: string[], folderId: string) => (await api.post<MediaItem[]>('/media/items/copy', { ids, folderId })).data,
  move: async (ids: string[], folderId: string) => (await api.post<MediaItem[]>('/media/items/move', { ids, folderId })).data,
  /** DELETE with a body (the JSON client sends a body only for its own verbs). */
  remove: async (ids: string[]) => {
    const res = await authedFetch('/media/items', { method: 'DELETE', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ ids }) });
    const payload = await res.json().catch(() => null);
    if (!res.ok) throw new ApiRequestError(res.status, payload?.error ?? { code: 'NETWORK', message: res.statusText });
    return payload?.data as { deleted: number };
  },
};

/** The tree's folders, flattened, by id. */
export function indexTree(roots: MediaFolder[]): Map<string, MediaFolder> {
  const out = new Map<string, MediaFolder>();
  const walk = (f: MediaFolder) => { out.set(f.id, f); f.children?.forEach(walk); };
  roots.forEach(walk);
  return out;
}

// ─── Upload with progress ───────────────────────────────────────────────────
/**
 * One file to a folder, as multipart, with its label and caption; `onProgress` gets 0..1. XHR because fetch cannot
 * report upload progress. A stale session is refreshed first by a cheap JSON call (the API client refreshes on 401).
 */
export function uploadFile(folderId: string, file: File, meta: { label?: string | null; caption?: string | null }, onProgress: (f: number) => void): { promise: Promise<MediaItem[]>; abort: () => void } {
  const xhr = new XMLHttpRequest();
  const promise = (async () => {
    await api.get('/media/labels');      // refreshes the token if it has expired
    return new Promise<MediaItem[]>((resolve, reject) => {
      const form = new FormData();
      if (meta.label) form.append('label', meta.label);
      if (meta.caption) form.append('caption', meta.caption);
      form.append('file', file, file.name);
      xhr.open('POST', apiUrl(`/media/folders/${folderId}/items`));
      const token = tokenStore.get()?.accessToken;
      if (token) xhr.setRequestHeader('authorization', `Bearer ${token}`);
      xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
      xhr.onload = () => {
        let body: { data?: MediaItem[]; error?: { code: string; message: string } } | null = null;
        try { body = JSON.parse(xhr.responseText); } catch { /* not JSON */ }
        if (xhr.status >= 200 && xhr.status < 300) { onProgress(1); resolve(body?.data ?? []); }
        else reject(new ApiRequestError(xhr.status, body?.error ?? { code: 'UPLOAD_FAILED', message: xhr.statusText || 'Upload failed' }));
      };
      xhr.onerror = () => reject(new ApiRequestError(0, { code: 'NETWORK', message: 'The connection dropped during the upload' }));
      xhr.onabort = () => reject(new ApiRequestError(0, { code: 'ABORTED', message: 'Cancelled' }));
      xhr.send(form);
    });
  })();
  return { promise, abort: () => xhr.abort() };
}

// ─── Files in (thumbnails, previews) ────────────────────────────────────────
// Files are private: fetched with the session token and shown from object URLs. An item's bytes never change, so a
// thumbnail fetched once is kept for the session (bounded, oldest dropped first).
const blobCache = new Map<string, Promise<string>>();
const CACHE_MAX = 600;
function cachedBlobUrl(url: string): Promise<string> {
  const hit = blobCache.get(url);
  if (hit) return hit;
  const p = (async () => {
    const res = await authedFetch(url);
    if (!res.ok) throw new Error(String(res.status));
    return URL.createObjectURL(await res.blob());
  })();
  p.catch(() => blobCache.delete(url));
  blobCache.set(url, p);
  if (blobCache.size > CACHE_MAX) {
    const [oldest] = blobCache.keys();
    if (oldest) { const old = blobCache.get(oldest); blobCache.delete(oldest); void old?.then((u) => URL.revokeObjectURL(u), () => undefined); }
  }
  return p;
}

/** An object URL for a stored file (cached), or null while it loads; `error` when it could not be fetched. */
export function useMediaBlob(url: string | null | undefined): { src: string | null; error: boolean } {
  const [state, setState] = useState<{ url: string | null; src: string | null; error: boolean }>({ url: null, src: null, error: false });
  useEffect(() => {
    if (!url) return;
    let live = true;
    cachedBlobUrl(url).then((src) => { if (live) setState({ url, src, error: false }); }, () => { if (live) setState({ url, src: null, error: true }); });
    return () => { live = false; };
  }, [url]);
  return state.url === url ? { src: state.src, error: state.error } : { src: null, error: false };
}

/** Like useMediaBlob but not kept: for a large file (a video, a PDF) seen once in the preview. */
export function useMediaFile(url: string | null | undefined): { src: string | null; error: boolean } {
  const [state, setState] = useState<{ url: string | null; src: string | null; error: boolean }>({ url: null, src: null, error: false });
  useEffect(() => {
    if (!url) return;
    let live = true; let href: string | null = null;
    void (async () => {
      try {
        const res = await authedFetch(url);
        if (!res.ok) throw new Error(String(res.status));
        href = URL.createObjectURL(await res.blob());
        if (live) setState({ url, src: href, error: false }); else URL.revokeObjectURL(href);
      } catch { if (live) setState({ url, src: null, error: true }); }
    })();
    return () => { live = false; if (href) URL.revokeObjectURL(href); };
  }, [url]);
  return state.url === url ? { src: state.src, error: state.error } : { src: null, error: false };
}

// ─── Files out (downloads) ──────────────────────────────────────────────────
function saveBlob(blob: Blob, name: string) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 30_000);
}
const nameFrom = (res: Response, fallback: string) => {
  const cd = res.headers.get('content-disposition') ?? '';
  const star = /filename\*=UTF-8''([^;]+)/i.exec(cd)?.[1];
  if (star) { try { return decodeURIComponent(star); } catch { /* fall through */ } }
  return /filename="([^"]+)"/.exec(cd)?.[1] ?? fallback;
};
async function failure(res: Response): Promise<never> {
  const payload = await res.json().catch(() => null);
  throw new ApiRequestError(res.status, payload?.error ?? { code: 'DOWNLOAD_FAILED', message: res.statusText || 'Download failed' });
}

/** Save one file under its own name. */
export async function downloadItem(item: MediaItem) {
  const res = await authedFetch(item.downloadUrl);
  if (!res.ok) await failure(res);
  saveBlob(await res.blob(), nameFrom(res, item.fileName));
}
/** Save a zip of a selection or of a whole folder (with its sub-folders). */
export async function downloadZip(body: { ids: string[] } | { folderId: string }) {
  const res = await authedFetch('/media/download', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  if (!res.ok) await failure(res);
  saveBlob(await res.blob(), nameFrom(res, 'media.zip'));
}

// ─── Clipboard ──────────────────────────────────────────────────────────────
/** Copied or cut files, kept while staff move between folders (and between the Media page and an Images tab). */
interface Clip { ids: string[]; mode: 'copy' | 'cut'; fromFolderId: string; set: (c: { ids: string[]; mode: 'copy' | 'cut'; fromFolderId: string }) => void; clear: () => void }
export const useMediaClipboard = create<Clip>((set) => ({
  ids: [], mode: 'copy', fromFolderId: '',
  set: (c) => set(c),
  clear: () => set({ ids: [], mode: 'copy', fromFolderId: '' }),
}));
